/**
 * Zentrale TypeScript-Typen für die Stundenliste-Anwendung
 */

import type { FederalState } from '../data/germanHolidays';

/**
 * Eintrag für einen einzelnen Tag
 */
export interface DayEntry {
  /** Datum im ISO-Format (YYYY-MM-DD) */
  date: string;

  /** Gearbeitete Stunden (0-24, Dezimalzahlen erlaubt) */
  hours: number;

  /** Ist dieser Tag ein Feiertag? */
  isHoliday: boolean;

  /** Wurde an diesem Tag Urlaub genommen? */
  isVacation: boolean;

  /** Wurde an diesem Tag ein halber Urlaubstag genommen? (zählt 0.5) */
  isHalfVacation?: boolean;

  /** Ist dieser Tag ein Betriebsferientag? (gesperrt, kein Urlaub) */
  isCompanyVacation?: boolean;

  /** Ist dieser Tag ein Krankheitstag? */
  isSickDay?: boolean;

  /**
   * Berechnete Stunden für Krankheitstage (nach EFZG).
   * Wird beim ersten Tag einer Krankheitsphase berechnet und für
   * alle folgenden Tage der Phase übernommen.
   */
  sickDayCalculatedHours?: number;

  /**
   * Berechnete Stunden für Urlaubstage (nach §11 BUrlG).
   * Wird beim ersten Tag einer Urlaubsphase berechnet und für
   * alle folgenden Tage der Phase übernommen.
   */
  vacationCalculatedHours?: number;

  /** Abwesenheitsart-ID (optional, FK auf AbsenceType) */
  absenceTypeId?: string | null;

  /**
   * Marker: Es existiert eine Abwesenheit fuer diesen Tag, auch wenn
   * absenceTypeId aus DSGVO-Gruenden (Manager-Sicht auf OCCUPIED_ONLY)
   * im DTO auf null gesetzt wurde. Vom Backend (entryToResponse) gesetzt.
   */
  isAbsence?: boolean;

  /** Optionale Bemerkung (max. 200 Zeichen) */
  note?: string;

  /** Manager-Anmerkung (nur von Abteilungsleitern editierbar) */
  managerNote?: string;

  /** ID des Managers, der die Anmerkung erstellt hat */
  managerNoteAuthorId?: string;

  /** Name des Managers, der die Anmerkung erstellt hat */
  managerNoteAuthorName?: string;

  /** Zeitstempel der Manager-Anmerkung */
  managerNoteTimestamp?: string;
}

/**
 * Abteilungen im Unternehmen
 */
export type Department =
  | 'SEO'
  | 'SEA'
  | 'Social'
  | 'Content';

/**
 * Beschäftigungsart (Prisma-Enum EmploymentType)
 */
export type EmploymentType =
  | 'INTERNSHIP'
  | 'MANDATORY_INTERNSHIP'
  | 'ORIENTATION_INTERNSHIP'
  | 'TEMPORARY'
  | 'WORKING_STUDENT'
  | 'APPRENTICE';

/** Auswählbare Beschäftigungsarten (ohne veraltetes INTERNSHIP) */
export const EMPLOYMENT_TYPES: EmploymentType[] = [
  'MANDATORY_INTERNSHIP',
  'ORIENTATION_INTERNSHIP',
  'TEMPORARY',
  'WORKING_STUDENT',
  'APPRENTICE',
];

/**
 * Lohnart
 */
export type WageType = 'HOURLY' | 'FIXED';

export const WAGE_TYPES: WageType[] = ['HOURLY', 'FIXED'];

export const WAGE_TYPE_LABELS: Record<WageType, string> = {
  HOURLY: 'Stundenlohn',
  FIXED: 'Fix',
};

/**
 * Monatliche Abrechnungsfreigabe
 */
export interface MonthlyApproval {
  userId: string;
  year: number;
  month: number;
  approved: boolean;
  approvalNote?: string | null;
  approvedBy?: string | null;
  approvedAt?: string | null;
}

/** Labels inkl. Fallback für veraltetes INTERNSHIP */
export const EMPLOYMENT_TYPE_LABELS: Record<string, string> = {
  INTERNSHIP: 'Praktikum (veraltet)',
  MANDATORY_INTERNSHIP: 'Pflichtpraktikum',
  ORIENTATION_INTERNSHIP: 'Orientierungspraktikum',
  TEMPORARY: 'Aushilfe',
  WORKING_STUDENT: 'Werkstudium',
  APPRENTICE: 'Ausbildung',
};

/**
 * Betriebseinstellungen
 */
export interface CompanySettings {
  /** Urlaubstage pro Jahr bei Vollzeit (20-40) */
  vacationDaysFullTime: number;

  /** Arbeitstage pro Woche bei Vollzeit (5 oder 6) */
  workDaysPerWeekFullTime: 5 | 6;

  /** Sollstunden pro Tag bei Vollzeit (4-12) */
  hoursPerDayFullTime: number;

  /** Brutto-Stundenlohn in Euro */
  hourlyWage: number;

  /** Bundesland für regionale Feiertage */
  federalState: FederalState;

  /** Abteilung des Mitarbeiters */
  department?: Department;
}

/**
 * Berechnungsergebnisse für die letzen 13 Wochen
 */
export interface Last13WeeksData {
  /** Gesamtstunden in den letzten 13 Wochen */
  totalHours: number;
  
  /** Anzahl Arbeitstage in den letzten 13 Wochen */
  workDays: number;
  
  /** Durchschnittliche Stunden pro Arbeitstag */
  averageHoursPerDay: number;
  
  /** Urlaubsentgelt pro Tag */
  vacationPayPerDay: number;
}

/**
 * Gesamte Berechnungsergebnisse
 */
export interface CalculationResults {
  /** Summe aller gearbeiteten Stunden */
  totalHours: number;

  /** Anzahl Tage mit Arbeitsstunden > 0 */
  workDays: number;

  /** Anzahl markierter Feiertage */
  holidays: number;

  /** Anzahl genommener Urlaubstage */
  vacationDaysTaken: number;

  /** Anzahl Krankentage */
  sickDays: number;

  /** Durchschnittliche Stunden pro Arbeitstag */
  averageHoursPerDay: number;

  /** Durchschnittliche Arbeitstage pro Woche */
  averageWorkDaysPerWeek: number;

  /** Berechneter Urlaubsanspruch (vor Rundung) */
  vacationEntitlementRaw: number;

  /** Urlaubsanspruch nach §5 BUrlG gerundet */
  vacationEntitlementRounded: number;

  /** Urlaubsübertrag aus dem Vorjahr (nicht verfallen) */
  carryOverFromPreviousYear: number;

  /** Gesamter Urlaubsanspruch inkl. Übertrag */
  totalVacationEntitlement: number;

  /** Verbleibender Resturlaub */
  remainingVacation: number;

  /** Daten für 13-Wochen-Berechnung */
  last13Weeks: Last13WeeksData;

  /** Volle Beschäftigungsmonate im Jahr (§5 BUrlG Teilurlaub, nur bei proRataFactor < 1) */
  fullEmploymentMonths?: number;

  /** Anteilsfaktor (fullEmploymentMonths / 12, nur bei Teilurlaub < 1) */
  proRataFactor?: number;

  /** Ganzjahres-Urlaubsanspruch gerundet (nur bei Teilurlaub, zum Vergleich) */
  vacationEntitlementFullYear?: number;

  /** Pro-rata erdiente Urlaubstage bis zum Referenzdatum (§5 BUrlG) */
  vacationEarnedToDate?: number;
}

/**
 * Monatliche Zusammenfassung
 */
export interface MonthlyData {
  /** Monat (1-12) */
  month: number;
  
  /** Jahr */
  year: number;
  
  /** Monatsname */
  monthName: string;
  
  /** Summe Arbeitsstunden */
  totalHours: number;
  
  /** Anzahl Arbeitstage */
  workDays: number;
  
  /** Anzahl Feiertage */
  holidays: number;
  
  /** Anzahl Urlaubstage */
  vacationDays: number;

  /** Anzahl Krankentage */
  sickDays: number;
}

/**
 * Urlaubsübertrag aus dem Vorjahr
 */
export interface VacationCarryOver {
  /** Jahr aus dem der Urlaub übertragen wurde */
  fromYear: number;

  /** Anzahl übertragener Urlaubstage */
  days: number;

  /** Verfallsdatum (31. März des Folgejahres) */
  expirationDate: string;

  /** Wurde der Übertrag bereits verfallen? */
  expired: boolean;
}

/**
 * Globaler App-State
 */
export interface AppState {
  /** Ausgewähltes Jahr */
  year: number;

  /** Betriebseinstellungen */
  settings: CompanySettings;

  /** Alle Tageseinträge (Key: ISO-Datum) */
  entries: Record<string, DayEntry>;

  /** Urlaubsübertrag aus dem Vorjahr */
  carryOverVacation: VacationCarryOver | null;

  /** Settings-Segmente für Urlaubsberechnung (aus History) */
  settingsSegments: SettingsSegment[];

  /** Wird gerade geladen? */
  isLoading: boolean;

  /** WageType des eingeloggten Benutzers (für Betriebsferien-Klick-Logik im eigenen Kalender) */
  userWageType?: WageType | null;
}

/**
 * Standard-Betriebseinstellungen
 */
export const DEFAULT_SETTINGS: CompanySettings = {
  vacationDaysFullTime: 20,
  workDaysPerWeekFullTime: 5,
  hoursPerDayFullTime: 8,
  hourlyWage: 13.90, // Mindestlohn ab 01.01.2026
  federalState: 'NI',
};

/**
 * Benutzer-Eintrag (für HR/Manager-Listen)
 */
export interface UserEntry {
  id: string;
  name: string;
  firstName?: string;
  lastName?: string;
  email: string;
  department?: Department;
  employmentType?: EmploymentType | null;
  isHR?: boolean;
  isManager?: boolean;
  employmentStart?: string | null;
  employmentEnd?: string | null;
  excludeFromExport?: boolean;
  personnelNumber?: string | null;
  rv?: boolean;
  hansefit?: boolean;
  contractualHours?: number | null;
  wageType?: WageType | null;
  monthlySalary?: number | null;
  /** true, sobald das Konto mit Azure verknüpft ist (Erst-Login erfolgt). Dann ist die E-Mail gesperrt. */
  azureLinked?: boolean;
  lastActive: string;
}

/**
 * Team-Mitglied mit Hierarchie-Informationen (fuer Manager-Dashboard)
 */
export interface TeamMemberEntry extends UserEntry {
  /** 1 = direkt, 2+ = indirekt */
  depth: number;
  /** ID des direkten Untergebenen ueber den die Verbindung laeuft (null bei depth=1) */
  viaManagerId: string | null;
  /** Name des direkten Untergebenen (null bei depth=1) */
  viaManagerName: string | null;
}

/**
 * Manager-Zuordnung (welcher Manager betreut welchen Mitarbeiter)
 */
export interface ManagerAssignment {
  id: string;
  userId: string;
  managerId: string;
  managerName?: string;
  managerEmail?: string;
}

/**
 * Daten für manuelles Anlegen eines neuen Mitarbeiters
 */
export interface CreateUserData {
  email: string;
  firstName: string;
  lastName: string;
  department?: Department | null;
  employmentType?: EmploymentType | null;
  employmentStart?: string | null;
  employmentEnd?: string | null;
  personnelNumber?: string | null;
  excludeFromExport?: boolean;
  wageType?: WageType | null;
  hourlyWage?: number | null;
  monthlySalary?: number | null;
  contractualHours?: number | null;
  rv?: boolean;
  hansefit?: boolean;
  settings?: {
    federalState?: string;
    vacationDaysFullTime?: number;
    workDaysPerWeekFullTime?: number;
    hoursPerDayFullTime?: number;
    hourlyWage?: number | null;
  };
}

/**
 * Azure AD User-Suchergebnis
 */
export interface AzureUserResult {
  azureId: string;
  displayName: string;
  email: string;
  firstName: string;
  lastName: string;
  department?: string;
}

/**
 * Daten für Azure AD User-Import
 */
export interface ImportAzureUserData extends CreateUserData {
  azureId: string;
}

/**
 * Organigramm: Flache User-Daten vom Backend
 */
export interface OrgChartUser {
  id: string;
  name: string;
  department: string | null;
  managerIds: string[];
}

/**
 * Organigramm: Baum-Knoten (Frontend-seitig aufgebaut)
 */
export interface OrgNode {
  id: string;
  name: string;
  department: string | null;
  children: OrgNode[];
  managerIds: string[];
  primaryManagerId: string | null;
  additionalManagerIds: string[];
  isRoot?: boolean;
}

/**
 * Urlaubsanpassungs-Kategorie
 */
export type VacationAdjustmentCategory = 'SPECIAL_LEAVE' | 'EDUCATIONAL_LEAVE' | 'BONUS' | 'CORRECTION';

export const VACATION_ADJUSTMENT_CATEGORY_LABELS: Record<VacationAdjustmentCategory, string> = {
  SPECIAL_LEAVE: 'Sonderurlaub (Hochzeit, Geburt, Todesfall)',
  EDUCATIONAL_LEAVE: 'Bildungsurlaub',
  BONUS: 'Kulanz / Bonus-Tage',
  CORRECTION: 'Korrektur',
};

/**
 * Urlaubsanpassung (HR-erstellt)
 */
export interface VacationAdjustment {
  id: string;
  userId: string;
  year: number;
  days: number;
  category: VacationAdjustmentCategory;
  reason: string;
  createdBy: string;
  createdById: string;
  createdAt: string;
}

/**
 * Aggregierte Team-Statistiken pro User (Manager-/HR-Dashboard)
 */
export interface UserTeamStats {
  userId: string;
  userName: string;
  department: string | null;
  vacationEntitlement: number;
  vacationDaysTaken: number;
  vacationDaysRemaining: number;
  totalHours: number;
  workDays: number;
  sickDays: number;
}

/**
 * Sichtbarkeit eines AbsenceType in der Manager-Sicht (DSGVO Art. 9 / § 26 BDSG).
 * - FULL: Typ + Details sichtbar.
 * - OCCUPIED_ONLY: nur neutrales „abwesend"-Label, Typ verborgen.
 * - HR_ONLY: Eintrag fuer Manager komplett unsichtbar.
 */
export type ManagerVisibility = 'FULL' | 'OCCUPIED_ONLY' | 'HR_ONLY';

export const MANAGER_VISIBILITY_LABELS: Record<ManagerVisibility, string> = {
  FULL: 'Voll sichtbar (Typ + Details)',
  OCCUPIED_ONLY: 'Nur „abwesend" (Typ verborgen)',
  HR_ONLY: 'Nur für HR sichtbar',
};

export const MANAGER_VISIBILITY_OPTIONS: ManagerVisibility[] = ['FULL', 'OCCUPIED_ONLY', 'HR_ONLY'];

/**
 * Abwesenheitsart (HR-konfigurierbar)
 */
export interface AbsenceType {
  id: string;
  name: string;
  shortName: string | null;
  color: string;
  colorDark: string | null;
  icon: string | null;
  countsAsVacation: boolean;
  countsAsSickDay: boolean;
  isPaid: boolean;
  allowHalfDay: boolean;
  excludeFrom13WeekAvg: boolean;
  skipInVacationBooking: boolean;
  requiresApproval: boolean;
  /** Sichtbarkeit in der Manager-Sicht (DSGVO Art. 9). Default FULL. */
  managerVisibility: ManagerVisibility;
  isSystem: boolean;
  systemKey: string | null;
  sortOrder: number;
  active: boolean;
}

/**
 * System-Keys fuer die eingebauten Abwesenheitsarten.
 * Erweitert mit Migration 20260521110000_add_manager_visibility:
 * FULL: HALF_VACATION, SPECIAL_LEAVE, EDUCATIONAL_LEAVE, PLANNED_ABSENCE,
 *       OVERTIME_PAYOFF, UNPAID_LEAVE, PARENTAL_LEAVE.
 * OCCUPIED_ONLY: CHILD_SICK_DAY, REHAB, MATERNITY_PROTECTION,
 *                NURSING_TIME, QUARANTINE.
 * HR_ONLY: BEM_MEASURES.
 */
export type AbsenceTypeSystemKey =
  | 'VACATION'
  | 'SICK_DAY'
  | 'COMPANY_VACATION'
  | 'HOLIDAY'
  | 'HALF_VACATION'
  | 'SPECIAL_LEAVE'
  | 'EDUCATIONAL_LEAVE'
  | 'PLANNED_ABSENCE'
  | 'OVERTIME_PAYOFF'
  | 'UNPAID_LEAVE'
  | 'PARENTAL_LEAVE'
  | 'CHILD_SICK_DAY'
  | 'REHAB'
  | 'MATERNITY_PROTECTION'
  | 'NURSING_TIME'
  | 'QUARANTINE'
  | 'BEM_MEASURES';

/** Erlaubte Farbnamen für Abwesenheitsarten */
export const ABSENCE_TYPE_COLORS = [
  'blue', 'red', 'indigo', 'amber', 'purple', 'teal',
  'pink', 'orange', 'cyan', 'lime',
] as const;

export type AbsenceTypeColor = typeof ABSENCE_TYPE_COLORS[number];

/**
 * Leitet DayEntry-Flags aus einem AbsenceType ab.
 * Zentrale Logik — Frontend und Backend verwenden dieselbe Ableitung.
 */
export function deriveEntryFlags(
  absenceType: AbsenceType | null,
  isHalfDay: boolean,
): { isVacation: boolean; isHalfVacation: boolean; isSickDay: boolean } {
  if (!absenceType) {
    return { isVacation: false, isHalfVacation: false, isSickDay: false };
  }
  const effectiveHalfDay = isHalfDay && absenceType.allowHalfDay;
  return {
    isVacation: absenceType.countsAsVacation && !effectiveHalfDay,
    isHalfVacation: absenceType.countsAsVacation && effectiveHalfDay,
    isSickDay: absenceType.countsAsSickDay,
  };
}

// ===== Absence Request Types =====

/**
 * Status eines Abwesenheitsantrags
 */
export type AbsenceRequestStatus = 'REQUESTED' | 'APPROVED' | 'REJECTED' | 'CANCELLED' | 'PLANNED';

export const ABSENCE_REQUEST_STATUS_LABELS: Record<AbsenceRequestStatus, string> = {
  REQUESTED: 'Beantragt',
  APPROVED: 'Genehmigt',
  REJECTED: 'Abgelehnt',
  CANCELLED: 'Storniert',
  PLANNED: 'Geplant',
};

/**
 * Abwesenheitsantrag
 */
export interface AbsenceRequest {
  id: string;
  userId: string;
  absenceTypeId: string;
  absenceType?: AbsenceType;
  startDate: string;
  endDate: string;
  halfDayStart: boolean;
  halfDayEnd: boolean;
  totalDays: number;
  status: AbsenceRequestStatus;
  employeeNote?: string | null;
  reviewedBy?: string | null;
  reviewedByName?: string | null;
  reviewedAt?: string | null;
  reviewerNote?: string | null;
  submittedAt: string;
  createdAt: string;
  updatedAt: string;
  /** Joined User-Daten (bei Manager/HR-Endpunkten) */
  user?: {
    id: string;
    name: string;
    firstName: string;
    lastName: string;
    department: string | null;
  };
}

/**
 * Daten für die Erstellung eines Abwesenheitsantrags
 */
export interface CreateAbsenceRequestData {
  absenceTypeId: string;
  startDate: string;
  endDate: string;
  halfDayStart?: boolean;
  halfDayEnd?: boolean;
  employeeNote?: string | null;
}

/**
 * Betriebsferien-Regel (firmenweit, wiederkehrend)
 */
export interface CompanyVacationRule {
  id: string;
  startDay: number;
  startMonth: number;
  endDay: number;
  endMonth: number;
  active: boolean;
}

// ===== Notification Preferences =====

export type NotificationChannel = 'EMAIL' | 'TEAMS_CHANNEL' | 'TEAMS_DM';

export type NotificationEventType =
  | 'ABSENCE_SUBMITTED'
  | 'ABSENCE_APPROVED'
  | 'ABSENCE_REJECTED'
  | 'ABSENCE_CANCELLED';

export const NOTIFICATION_CHANNEL_LABELS: Record<NotificationChannel, string> = {
  EMAIL: 'E-Mail',
  TEAMS_CHANNEL: 'Teams-Sammelkanal',
  TEAMS_DM: 'Teams-Direktnachricht',
};

export const NOTIFICATION_EVENT_LABELS: Record<NotificationEventType, string> = {
  ABSENCE_SUBMITTED: 'Neuer Abwesenheitsantrag (für Freigeber)',
  ABSENCE_APPROVED: 'Mein Antrag wurde genehmigt',
  ABSENCE_REJECTED: 'Mein Antrag wurde abgelehnt',
  ABSENCE_CANCELLED: 'Ein Antrag wurde storniert (für Freigeber)',
};

export interface NotificationPreference {
  channel: NotificationChannel;
  eventType: NotificationEventType;
  enabled: boolean;
}

// ===== MFA Types =====

/**
 * MFA-Status eines Users
 */
export type MfaStatus = 'active' | 'grace_period' | 'overdue' | 'enforced' | 'disabled';

export const MFA_STATUS_LABELS: Record<MfaStatus, string> = {
  active: 'Aktiv',
  grace_period: 'Grace Period',
  overdue: 'Überfällig',
  enforced: 'Erzwungen',
  disabled: 'Deaktiviert',
};

/**
 * MFA-Status-Antwort vom Backend
 */
export interface MfaStatusResponse {
  mfaEnabled: boolean;
  mfaEnforced: boolean;
  graceExpired: boolean;
  graceDaysRemaining: number;
  graceUntil: string | null;
  recoveryEmail: string | null;
  remainingRecoveryCodes: number;
  devices: Array<{
    id: string;
    label: string;
    lastUsedAt: string | null;
    createdAt: string;
  }>;
  entraMfaVerified?: boolean;
}

/**
 * MFA-Setup-Initiate-Antwort
 */
export interface MfaSetupInitiateResponse {
  deviceId: string;
  secret: string;
  qrDataUrl: string;
  otpauthUri: string;
}

/**
 * MFA Admin-Übersicht pro User
 */
export interface MfaAdminUserStatus {
  userId: string;
  name: string;
  mfaEnabled: boolean;
  status: MfaStatus;
  graceUntil: string | null;
}

// ===== Employment History Types =====

/**
 * History-Segment: Vertragsdaten für einen Zeitraum (Frontend-Typ, Daten serialisiert als Strings)
 */
export interface HistorySegment {
  from: string;    // YYYY-MM-DD
  to: string;      // YYYY-MM-DD
  values: {
    employmentType: string | null;
    wageType: string | null;
    hourlyWage: number | null;
    monthlySalary: number | null;
    contractualHours: number | null;
    department: string | null;
    rv: boolean;
    hansefit: boolean;
    // Settings-Felder (ab Phase 4a)
    federalState: string;
    vacationDaysFullTime: number;
    workDaysPerWeekFullTime: number;
    hoursPerDayFullTime: number;
  };
}

/**
 * Settings-Segment für Urlaubsberechnung (Teilurlaub nach §5 BUrlG)
 */
export interface SettingsSegment {
  from: string;    // YYYY-MM-DD
  to: string;      // YYYY-MM-DD
  values: {
    vacationDaysFullTime: number;
    workDaysPerWeekFullTime: number;
    hoursPerDayFullTime: number;
    federalState: string;
    wageType?: string | null;
  };
}

/**
 * Employment History Record (vollständig, für Timeline-Anzeige)
 */
export interface EmploymentHistoryRecord {
  id: string;
  userId: string;
  validFrom: string;        // YYYY-MM-DD
  validTo: string | null;   // YYYY-MM-DD oder null

  // Kategorie A: Vertragsdaten
  employmentType: EmploymentType | null;
  wageType: WageType | null;
  hourlyWage: number | null;
  monthlySalary: number | null;
  contractualHours: number | null;
  department: string | null;
  rv: boolean;
  hansefit: boolean;

  // Settings-Felder (ab Phase 4a)
  federalState?: string | null;
  vacationDaysFullTime?: number | null;
  workDaysPerWeekFullTime?: number | null;
  hoursPerDayFullTime?: number | null;

  // Audit
  changedByName: string | null;
  changeNote: string | null;
  createdAt: string;
}

/**
 * Request-Body für History-Record erstellen
 */
export interface CreateHistoryRequest {
  validFrom: string;
  employmentType?: EmploymentType | null;
  wageType?: WageType | null;
  hourlyWage?: number | null;
  monthlySalary?: number | null;
  contractualHours?: number | null;
  department?: string | null;
  rv?: boolean;
  hansefit?: boolean;
  // Settings-Felder (ab Phase 4a)
  federalState?: string;
  vacationDaysFullTime?: number;
  workDaysPerWeekFullTime?: number;
  hoursPerDayFullTime?: number;
  changeNote?: string;
}

/**
 * Validierungsgrenzen
 */
export const VALIDATION = {
  hours: { min: 0, max: 24 },
  vacationDays: { min: 20, max: 40 },
  hoursPerDay: { min: 4, max: 12 },
  hourlyWage: { min: 0, max: 999.99 },
  noteMaxLength: 200,
} as const;
